import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '@/providers/TenantProvider';
import { cn } from '@/shared/lib/cn';

export function TenantSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { isElevated, availableTenants, activeTenant, activeTenantId, setActiveTenant } =
    useTenant();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableTenants;
    return availableTenants.filter(
      (tenant) =>
        tenant.name.toLowerCase().includes(q) ||
        tenant.display_name.toLowerCase().includes(q) ||
        tenant.slug.toLowerCase().includes(q),
    );
  }, [availableTenants, query]);

  if (!isElevated) return null;

  return (
    <div
      className={cn('relative', className)}
      role="combobox"
      aria-expanded={open}
      aria-label={t('tenant.switcher.label')}
    >
      <button
        type="button"
        className="flex max-w-[6.5rem] items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1.5 text-xs sm:max-w-[9rem] sm:gap-2 sm:px-3 sm:text-sm md:max-w-[12rem]"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
      >
        <span className="max-w-[12rem] truncate">
          {activeTenant
            ? activeTenant.display_name
            : activeTenantId
              ? t('tenant.switcher.selected')
              : t('tenant.switcher.placeholder')}
        </span>
      </button>
      {open && (
        <div className="absolute end-0 z-50 mt-1 w-[min(18rem,calc(100vw-2rem))] rounded-md border border-border bg-card p-2 shadow-lg sm:end-auto sm:w-72">
          <input
            type="search"
            className="mb-2 w-full rounded border border-border px-2 py-1 text-sm"
            placeholder={t('tenant.switcher.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('tenant.switcher.search')}
          />
          <ul role="listbox" className="max-h-60 overflow-auto">
            {filtered.map((tenant) => (
              <li key={tenant.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={tenant.id === activeTenantId}
                  className="flex w-full items-center gap-2 rounded px-2 py-2 text-start text-sm hover:bg-muted"
                  onClick={() => {
                    setActiveTenant(tenant.id);
                    setOpen(false);
                  }}
                >
                  {tenant.logo_url ? (
                    <img src={tenant.logo_url} alt="" className="h-6 w-6 rounded" />
                  ) : (
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-muted text-xs">
                      {tenant.name.slice(0, 1)}
                    </span>
                  )}
                  <span className="flex-1">
                    <span className="block font-medium">{tenant.display_name}</span>
                    <span className="text-xs text-muted-foreground">{tenant.status}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
